/**
 * Wire → view-model mappers. The M/P services emit snake_case records; the UI
 * works in normalized camelCase view models. Keeping the mapping in one place
 * means the whole app sees a consistent shape and the raw contract stays
 * faithful to the OpenAPI spec.
 *
 * No engineering/economic math happens here — only field renaming, coalescing
 * of the free-form `properties` bag, and normalization of enum-ish strings.
 */
import type { WellOut, WellView, WellStatus, WellPurpose } from "./types";

const STATUS_MAP: Record<string, WellStatus> = {
  drilling: "drilling",
  drill: "drilling",
  completion: "completion",
  completing: "completion",
  producing: "producing",
  production: "producing",
  suspended: "suspended",
  idle: "suspended",
  "p&a": "suspended",
};

const PURPOSE_MAP: Record<string, WellPurpose> = {
  development: "development",
  dev: "development",
  exploration: "exploration",
  expl: "exploration",
  appraisal: "appraisal",
  injection: "injection",
  injector: "injection",
};

function normStatus(raw: string | null | undefined): WellStatus | null {
  if (!raw) return null;
  return STATUS_MAP[raw.trim().toLowerCase()] ?? null;
}

function normPurpose(raw: string | null | undefined): WellPurpose | null {
  if (!raw) return null;
  return PURPOSE_MAP[raw.trim().toLowerCase()] ?? null;
}

function normReservoir(raw: unknown): "conventional" | "unconventional" | null {
  if (typeof raw !== "string") return null;
  const v = raw.trim().toLowerCase();
  if (v.startsWith("unconv") || v === "shale" || v === "tight") return "unconventional";
  if (v.startsWith("conv")) return "conventional";
  return null;
}

function num(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** Map a raw WellOut (+ its open `properties` bag) into the explorer view model. */
export function toWellView(w: WellOut): WellView {
  const p = w.properties ?? {};
  return {
    id: w.id,
    commonName: w.common_name,
    uwi: w.uwi,
    apiNo: w.api14,
    field: w.field ?? "—",
    basin: typeof p.basin === "string" ? p.basin : "—",
    operator: w.operator ?? "—",
    purpose: normPurpose(p.purpose),
    status: normStatus(w.status),
    statusRaw: w.status,
    reservoirType: normReservoir(p.reservoir_type),
    formation: typeof p.formation === "string" ? p.formation : null,
    totalDepthM: num(p.td_m),
    lat: num(p.lat),
    lon: num(p.lon),
  };
}
